import {mergeFeatureGeometries} from '@apps-common/geocorn-sdk/geom/ops';
import {
  isGeometryClean,
  isTooComplex,
  isValidGeometry,
} from '@apps-common/geocorn-sdk/geom/simplify';
import importer, {
  EXTENSIONS as SUPPORTED_EXTENSIONS,
  projTransform,
} from '@apps-common/importer';
import {DialogTitle} from '@apps-common/ui-components';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import FileIcon from '@mui/icons-material/Description';
import SelectAllIcon from '@mui/icons-material/PlaylistAdd';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import {bbox} from '@turf/bbox';
import groupBy from 'lodash/groupBy';
import numeral from 'numeral';
import {containsExtent} from 'ol/extent';
import GeoJsonFormat from 'ol/format/GeoJSON';
import {EXTENT as WORLD_EXTENT} from 'ol/proj/epsg4326';
import PropTypes from 'prop-types';
import React from 'react';
import Dropzone from 'react-dropzone';
import {Trans, withTranslation} from 'react-i18next';
import {connect} from 'react-redux';

import {FeaturePicker} from './feature-picker.jsx';
import HelpContent from './help.jsx';
import SimplifyFeature from './simplify.jsx';
import validateGeometry from './validate-geometry.js';
import {defineGeometry} from '@explorer/actions/search.js';
import {trackEvent} from '@explorer/analytics';
import {MODALS} from '@explorer/constants/index.js';
import {getProjection} from '@explorer/util/projection.js';

const MAX_FILE_SIZE = 5000000;
const MAX_FEATURES = 5000; // dont "@" me.

const STEPS = {
  START: 0,
  FEATURES: 1,
  HELP: -1,
  SIMPLIFY: 2,
};

const SUBHEADERS = {
  [STEPS.FEATURES]: <Trans i18nKey="uploadDialog.subheader.selectGeometries" />,
  [STEPS.HELP]: <Trans i18nKey="uploadDialog.subheader.importGuidelines" />,
  [STEPS.SIMPLIFY]: (
    <Trans
      i18nKey="uploadDialog.subheader.selectSimplified"
      components={{
        helpLink: (
          <Link
            href="https://docs.planet.com/develop/apis/features/uploading-and-validating-features/"
            target="_blank"
            rel="noopener noreferrer"
            underline="always"
            color="primary"
          />
        ),
      }}
    />
  ),
};

const SHAPEFILE_COMPONENTS = ['dbf', 'prj', 'shx', 'shp'];

const getExtension = path => path.toLowerCase().split('.').pop();

const hasProjection = files => {
  const exts = files.map(f => getExtension(f.name));
  const unknownPrj = exts.indexOf('shp') >= 0 || exts.indexOf('wkt') >= 0;
  const prj = exts.indexOf('prj') >= 0;
  return prj || !unknownPrj;
};

const areFilesTooLarge = files =>
  files.map(file => file.size).reduce((a, x) => a + x, 0) >= MAX_FILE_SIZE;

const MAX_VERTEX_COUNT = 1500;

class UploadDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      step: STEPS.START,
      acceptedFiles: [],
      showWaiting: false,
      errors: [],
      warnings: [],
      features: [],
      simplificationCandidates: [],
      selectedSimplificationCandidate: 0,
      isLoading: false,
    };

    this.dismissClose = this.dismissClose.bind(this);
    this.selectSimplificationCandidate =
      this.selectSimplificationCandidate.bind(this);
    this.geometryValidatorAbort = new AbortController();
  }

  updateFilesList(inFiles) {
    // do a pre-flight check on the files...
    const goodFiles = [];
    const badFiles = [];
    for (let i = 0, ii = inFiles.length; i < ii; i++) {
      const file = inFiles[i];
      if (SUPPORTED_EXTENSIONS.indexOf(getExtension(file.name)) >= 0) {
        // Avoids adding the same (duplicate) files
        if (goodFiles.every(gFile => gFile.name !== file.name)) {
          goodFiles.push(file);
        }
      } else {
        badFiles.push(file);
      }
    }

    // post up a error message to the user including
    //  files that are ignored.
    const errors = [];
    if (badFiles.length > 0) {
      errors.push(
        this.props.t('uploadDialog.unsupportedFilesError', {
          badFiles: badFiles
            .map(f => f.name)
            .join(this.props.t('global.listJoin')),
        })
      );
    }

    const group = groupBy(goodFiles, file => {
      const ext = getExtension(file.name);
      const name = file.name.toLowerCase().split('.')[0].replaceAll(' ', '_');

      if (SHAPEFILE_COMPONENTS.includes(ext)) {
        return `shp_${name}`;
      }
      return ext;
    });

    const groupKeys = Object.keys(group);

    const warnings = [];
    let filesTooLarge = false;
    if (goodFiles.length) {
      if (groupKeys[0].startsWith('shp')) {
        filesTooLarge = areFilesTooLarge(group[groupKeys[0]]);
      } else {
        filesTooLarge = areFilesTooLarge([goodFiles[0]]);
      }
    }

    if (goodFiles.length > 1) {
      if (groupKeys[0].startsWith('shp')) {
        warnings.push(
          this.props.t('uploadDialog.warningMultiFile', {
            fileNames: group[groupKeys[0]]
              .map(file => file.name)
              .join(this.props.t('global.listJoin')),
          })
        );
      } else {
        warnings.push(
          this.props.t('uploadDialog.warningSingleFile', {
            fileName: goodFiles[0].name,
          })
        );
      }
    }
    if (filesTooLarge) {
      warnings.push(this.props.t('uploadDialog.warningFileTooLarge'));
    }

    trackEvent('Upload Dialog: Added files', {
      totalAcceptedFiles: goodFiles.length,
      acceptedExtensions: goodFiles.map(file => getExtension(file.name)),
      rejectedExtensions: badFiles.map(file => getExtension(file.name)),
    });

    this.setState({
      acceptedFiles: goodFiles,
      errors,
      warnings,
    });
  }

  renderDropZone() {
    return (
      <Dropzone
        onDrop={files => {
          this.updateFilesList(this.state.acceptedFiles.concat(files));
        }}
      >
        {({getRootProps, getInputProps, isDragActive}) => (
          <>
            <Box
              {...getRootProps()}
              sx={{
                boxShadow: 'none',
                padding: '20px',
                borderWidth: 2,
                borderStyle: 'dashed',
                borderRadius: 1,
                borderColor: isDragActive ? 'primary.main' : 'text.secondary',
                backgroundColor: theme =>
                  isDragActive
                    ? `rgba(${theme.vars.palette.primary.mainChannel} / ${theme.vars.palette.action.hoverOpacity})`
                    : `rgba(${theme.vars.palette.primary.mainChannel} / ${theme.vars.palette.action.activatedOpacity})`,
                outline: 'none',
                cursor: 'pointer',
                transition: 'color .24s ease-in-out, border .24s ease-in-out',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: this.state.acceptedFiles.length > 0 ? '45%' : '100%',
                minHeight: '45%',
                flex: '1 1 auto',
              }}
            >
              <input {...getInputProps()} />
              <Typography
                gutterBottom
                variant="h6"
                align="center"
                sx={{
                  marginBottom: 2,
                }}
              >
                {this.props.t('uploadDialog.dragMessage')}
              </Typography>
              <Typography align="center">
                {this.props.t('uploadDialog.supportedFiles', {
                  extensions: SUPPORTED_EXTENSIONS.join(', '),
                })}
              </Typography>
            </Box>
            {this.state.acceptedFiles.length > 0 && (
              <Box
                sx={{
                  flexGrow: 1,
                  marginTop: 1,
                }}
              >
                <List dense sx={{overflowY: 'auto', maxHeight: 300}}>
                  {this.state.acceptedFiles.map((file, idx) => (
                    <ListItem
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${file.name}_${idx}`}
                      sx={{minWidth: '50%', maxWidth: '75%'}}
                      secondaryAction={
                        <IconButton
                          onClick={() => {
                            const files = this.state.acceptedFiles.slice();
                            files.splice(idx, 1);
                            this.updateFilesList(files);
                          }}
                        >
                          <ClearOutlinedIcon />
                        </IconButton>
                      }
                    >
                      <ListItemIcon>
                        <FileIcon />
                      </ListItemIcon>
                      <ListItemText>
                        {file.name}
                        <Typography
                          variant="caption"
                          component="span"
                          sx={{marginLeft: 1.5}}
                        >
                          ({numeral(file.size).format('0.[0] b')})
                        </Typography>
                      </ListItemText>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </>
        )}
      </Dropzone>
    );
  }

  importFiles(files) {
    this.setState({
      showWaiting: true,
      errors: [],
      warnings: [],
    });
    importer(files || this.state.acceptedFiles)
      .then(collection => {
        // A bit of a malformed GeoJSON file.
        // The real standard does not allow for this but an earlier
        //  version did. This tries to parse the EPSG code but will
        //  otherwise reject the file.
        if (collection.crs) {
          // this is likely a GeoJSON or a EWKT that came in
          //  with an EPSG code. Importer does not handle that
          //  directly.
          const crs = collection.crs;

          // log which CRS is being used.
          trackEvent('Upload Dialog: User defined projection', {
            name: crs.name,
            properties: crs.properties,
          });

          if (crs.type === 'name' && crs.properties && crs.properties.name) {
            const name = crs.properties.name;
            if (name.indexOf('EPSG') >= 0) {
              // The CRS code can come in as EPSG:4326 OR as EPSG::4326,
              //  using just ':' and using pop() ensures the number is
              //  returned.
              const code = crs.properties.name.split(':').pop();

              // do not do any work if the projection is already correct.
              if (code === '4326') {
                return collection;
              }

              return getProjection(code).then(prjText =>
                projTransform(prjText, collection)
              );
              // this is the "standard" WGS84 projection,
              //   no reprojection needed.
            } else if (name.indexOf('CRS84') > 0) {
              return collection;
            }
          }
          // the projection is bad/unknown, toss an error.
          throw new Error('Malformed GeoJson, carries an unknown CRS');
        } else {
          // The projection step was only offering WGS84 and Web Mercator,
          //  assume that if the collection of features is within the
          //  WGS84 boundaries, it is WGS84, otherwise, reproject.
          const collectionBBox = bbox(collection);
          if (!containsExtent(WORLD_EXTENT, collectionBBox)) {
            return getProjection('3857').then(prjText =>
              projTransform(prjText, collection)
            );
          }
        }
        // no projection hint, no worry.
        return collection;
      })
      .then(collection => {
        const collectionBBox = bbox(collection);
        if (!containsExtent(WORLD_EXTENT, collectionBBox)) {
          throw new Error(this.props.t('uploadDialog.invalidUpload'));
        }
        return collection;
      })
      .then(collection => {
        // filter out the features that do not work with Explorer
        const features = collection.features
          .filter(
            feature =>
              feature.geometry &&
              (feature.geometry.type === 'Polygon' ||
                feature.geometry.type === 'MultiPolygon') &&
              // the importer does not reject features, and
              //  occasionally will let some through without a geometry.
              feature.geometry.coordinates.length > 0
          )
          .slice(0, MAX_FEATURES);

        trackEvent('Upload Dialog: Uploaded a file', {
          totalCount: features.length,
        });

        if (
          features.length === 1 &&
          isGeometryClean(features[0].geometry, MAX_VERTEX_COUNT)
        ) {
          // Set the dialog back to upload when only importing a single feature
          this.setState({step: STEPS.START, acceptedFiles: []});

          // TODO: Should the user be toasted there was only one feature?
          this.props.defineGeometry({geometry: features[0].geometry});
          this.props.onClose();
        } else {
          const selectedFeatures = {};
          for (let i = 0, ii = features.length; i < ii; i++) {
            selectedFeatures[i] = true;
          }
          const nextState = {
            step: STEPS.FEATURES,
            selectedFeatures,
            features,
          };

          const warnings = [];
          const errors = [];

          // this warning message is keyed to the original count of
          //  features not the filtered version.
          if (collection.features.length > MAX_FEATURES) {
            warnings.push(
              this.props.t(
                'uploadDialog.maxFeaturesWarning',
                'Imported file contained more than {{max}} features. Showing the first {{max}} below.',
                {max: MAX_FEATURES}
              )
            );
          }

          if (features.length === 0) {
            errors.push(
              this.props.t(
                'uploadDialog.noValidFeaturesError',
                'No valid features found.'
              )
            );
          } else if (collection.importer) {
            if (collection.importer.datasetCount > 1) {
              warnings.push(
                this.props.t(
                  'uploadDialog.multipleDatasetsWarning',
                  'Imported file(s) contained multiple datasets, only features from the first are included.'
                )
              );
            }

            if (
              collection.importer.geometryTypes.filter(
                type => type.indexOf('Polygon') < 0
              ).length > 0
            ) {
              warnings.push(
                this.props.t(
                  'uploadDialog.nonPolygonWarning',
                  'Non-polygon features found. They have been excluded from the results.'
                )
              );
            }
          }

          if (warnings.length > 0) {
            nextState.warnings = warnings;
          }
          if (errors.length > 0) {
            nextState.errors = errors;
          }

          if (features.length === 1) {
            const geometry = features[0].geometry;
            const isValid = isValidGeometry(geometry);
            const isComplex = isTooComplex(geometry, MAX_VERTEX_COUNT);
            if (!isValid || isComplex) {
              nextState.step = STEPS.SIMPLIFY;
              nextState.isValid = isValid;
              nextState.isComplex = isComplex;
              nextState.importedGeometry = geometry;
            }
          }
          this.setState(nextState);
        }
      })
      .catch(err => {
        this.setState({errors: [err.message]});
      })
      .finally(() => {
        this.setState({showWaiting: false});
      });
  }

  getGeojsonGeometry() {
    const keys = Object.keys(this.state.selectedFeatures);
    const features = keys.map(idx => this.state.features[idx]);
    return features.length > 1
      ? mergeFeatureGeometries(features)
      : features[0].geometry;
  }

  importSelectedFeature() {
    const geometryObj = this.getGeojsonGeometry();

    trackEvent('Upload Dialog: Features selected', {
      selectedCount: Object.values(this.state.selectedFeatures).filter(
        val => !!val
      ).length,
      totalCount: this.state.features.length,
    });

    return validateGeometry(
      geometryObj,
      this.geometryValidatorAbort,
      MAX_VERTEX_COUNT
    )
      .then(({isValid, isComplex}) => {
        if (!isValid || isComplex) {
          this.setState({
            importedGeometry: geometryObj,
            isValid,
            isComplex,
            step: STEPS.SIMPLIFY,
          });
        } else {
          const format = new GeoJsonFormat();
          const geometry = format.readGeometryFromObject(geometryObj);
          try {
            this.props.defineGeometry({
              geometry: format.writeGeometryObject(geometry),
            });
            this.setState({acceptedFiles: []});
            this.props.onClose();
          } catch (err) {
            this.setState({errors: [err.message]});
          }
        }
      })
      .catch(() => {
        this.setState({
          errors: [this.props.t('uploadDialog.errorValidationFailed')],
        });
      });
  }

  importSimplifiedFeature() {
    const candidate = this.state.selectedSimplificationCandidate;

    trackEvent('Upload Dialog: Import simplified', {
      selectedSimplification: candidate.label,
    });

    try {
      this.setState({step: STEPS.START, acceptedFiles: []});
      this.props.defineGeometry({geometry: candidate?.geometry});
      this.props.onClose();
    } catch (err) {
      this.setState({errors: [err.message]});
    }
  }

  importOriginalGeometry() {
    trackEvent('Upload Dialog: Import original');
    const geometryObj = this.getGeojsonGeometry();
    try {
      this.setState({step: STEPS.START, acceptedFiles: []});
      this.props.defineGeometry({
        geometry: geometryObj,
        isGeometryTooComplex: true,
      });
      this.props.onClose();
    } catch (err) {
      this.setState({errors: [err.message]});
    }
  }

  showHelp() {
    this.setState({step: STEPS.HELP});
  }

  inFilesChanged(inFiles) {
    const hasPrj = hasProjection(inFiles);
    this.setState({
      step: STEPS.START,
      acceptedFiles: inFiles,
      errors: [],
    });

    if (hasPrj) {
      // jump to importing the files.
      this.importFiles(inFiles);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.inFiles !== this.props.inFiles) {
      this.inFilesChanged(this.props.inFiles);
    }
  }

  selectAll() {
    const selectedFeatures = {};
    for (let i = 0, ii = this.state.features.length; i < ii; i++) {
      selectedFeatures[i] = true;
    }

    trackEvent('Upload Dialog: Select all', {
      totalCount: this.state.features.length,
    });

    this.setState({
      ...this.state,
      selectedFeatures,
    });
  }

  selectNone() {
    trackEvent('Upload Dialog: Select none', {
      totalCount: this.state.features.length,
    });

    this.setState({
      ...this.state,
      selectedFeatures: {},
    });
  }

  resetWorker() {
    this.geometryValidatorAbort.abort();
  }

  stepBack() {
    let backStep = this.state.step - 1;
    // do not show the projection screen if it wasn't used
    if (backStep === STEPS.FEATURES && this.state.features.length === 1) {
      backStep -= 1;
    }

    this.resetWorker();

    this.setState({
      errors: [],
      warnings: [],
      step: backStep,
      isLoading: false,
    });
  }

  dismissClose(e) {
    trackEvent('Upload Dialog: Closed without AOI');
    this.resetWorker();
    this.props.onClose(e);
    this.setState({
      isLoading: false,
    });
  }

  selectSimplificationCandidate(selectedSimplificationCandidate) {
    this.setState({
      selectedSimplificationCandidate,
    });
  }

  render() {
    return (
      <Dialog
        open={this.props.open}
        fullWidth
        onClose={this.dismissClose}
        maxWidth="md"
        slotProps={{
          paper: {
            sx: {
              minHeight: 600,
            },
          },
        }}
      >
        <DialogTitle
          title={this.props.t('uploadDialog.title')}
          subheader={SUBHEADERS[this.state.step]}
          onClose={this.dismissClose}
        />
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {(this.state.errors.length > 0 || this.state.warnings.length > 0) && (
            <Box>
              {this.state.errors.length > 0 && (
                <Alert
                  severity="error"
                  data-qe="upload-dialog-error-msg"
                  sx={{marginBottom: 1}}
                >
                  {this.state.errors.map(errorMsg => (
                    <Typography variant="body2" key={errorMsg}>
                      {errorMsg}
                    </Typography>
                  ))}
                </Alert>
              )}
              {this.state.warnings.length > 0 && (
                <Alert
                  severity="warning"
                  data-qe="upload-dialog-warning-msg"
                  sx={{marginBottom: 1}}
                >
                  {this.state.warnings.map(warningMsg => (
                    <Typography variant="body2" key={warningMsg}>
                      {warningMsg}
                    </Typography>
                  ))}
                </Alert>
              )}
            </Box>
          )}

          {this.state.step === STEPS.HELP && <HelpContent />}
          {this.state.step === STEPS.START && this.renderDropZone()}
          {this.state.step === STEPS.FEATURES &&
            this.state.features.length > 1 && (
              <React.Fragment>
                <Box
                  sx={{
                    marginBottom: 1,
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  {Object.values(this.state.selectedFeatures).filter(
                    val => !!val
                  ).length === this.state.features.length ? (
                    <Button
                      color="primary"
                      variant="text"
                      onClick={() => this.selectNone()}
                      startIcon={<ClearAllIcon />}
                    >
                      {this.props.t('global.deselectAll')}
                    </Button>
                  ) : (
                    <Button
                      color="primary"
                      variant="text"
                      onClick={() => this.selectAll()}
                      startIcon={<SelectAllIcon />}
                    >
                      {this.props.t('global.selectAll')}
                    </Button>
                  )}
                </Box>
                <FeaturePicker
                  features={this.state.features}
                  selectedFeatures={this.state.selectedFeatures}
                  onChange={idx => {
                    // clone the current state.
                    const selected = {...this.state.selectedFeatures};
                    if (selected[idx] === true) {
                      delete selected[idx];
                    } else {
                      selected[idx] = true;
                    }
                    this.setState({selectedFeatures: selected});
                  }}
                />
              </React.Fragment>
            )}
          {this.state.step === STEPS.SIMPLIFY && (
            <SimplifyFeature
              originalGeometry={this.state.importedGeometry}
              isComplex={this.state.isComplex}
              isValid={this.state.isValid}
              onSelectCandidate={this.selectSimplificationCandidate}
              maxVertexCount={MAX_VERTEX_COUNT}
            />
          )}
        </DialogContent>
        <DialogActions
          sx={{
            /* This is only to support left-size justifying the back-button */
            position: 'relative',
          }}
        >
          {this.state.step === STEPS.HELP && (
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                this.setState({step: STEPS.START});
              }}
            >
              {this.props.t('uploadDialog.closeHelpButton')}
            </Button>
          )}
          {this.state.step > STEPS.START && (
            /* Left-side justify the back button */
            <Button
              sx={{
                position: 'absolute',
                left: 16,
              }}
              color="inherit"
              disabled={this.state.showWaiting}
              onClick={() => {
                this.stepBack();
              }}
              startIcon={<ArrowBackIcon />}
            >
              {this.props.t('uploadDialog.backButton')}
            </Button>
          )}
          {this.state.step === STEPS.START && (
            <Button
              color="primary"
              onClick={() => {
                this.showHelp();
              }}
              sx={{
                position: 'absolute',
                left: 16,
              }}
            >
              {this.props.t('uploadDialog.helpButton')}
            </Button>
          )}
          {this.state.step === STEPS.START && (
            <Button
              color="primary"
              variant="contained"
              disabled={
                this.state.acceptedFiles.length === 0 || this.state.showWaiting
              }
              onClick={() => {
                this.importFiles();
              }}
            >
              {this.state.showWaiting
                ? this.props.t('uploadDialog.pending', 'Importing...')
                : this.props.t('uploadDialog.importButton')}
            </Button>
          )}
          {this.state.step === STEPS.FEATURES &&
            this.state.features.length > 0 && (
              <React.Fragment>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    this.setState({isLoading: true});
                    this.importSelectedFeature().finally(() => {
                      this.setState({isLoading: false});
                    });
                  }}
                  disabled={
                    Object.values(this.state.selectedFeatures).filter(Boolean)
                      .length === 0 || this.state.isLoading
                  }
                  startIcon={
                    this.state.isLoading && <CircularProgress size={18} />
                  }
                >
                  {/* Google Translate issue: https://github.com/mui/material-ui/issues/27853 */}
                  <span>
                    {this.state.isLoading
                      ? this.props.t('uploadDialog.loading')
                      : this.props.t('uploadDialog.createButton', 'Create AOI')}
                  </span>
                </Button>
              </React.Fragment>
            )}
          {this.state.step === STEPS.SIMPLIFY && (
            <React.Fragment>
              <Button
                color="primary"
                variant="outlined"
                onClick={() => {
                  this.importOriginalGeometry();
                }}
              >
                {this.props.t('uploadDialog.useOriginal')}
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={() => {
                  this.importSimplifiedFeature();
                }}
              >
                {this.props.t('uploadDialog.useSelectedVersion')}
              </Button>
            </React.Fragment>
          )}
        </DialogActions>
      </Dialog>
    );
  }
}

UploadDialog.propTypes = {
  defineGeometry: PropTypes.func,
  onClose: PropTypes.func,
  t: PropTypes.func,
  classes: PropTypes.object,
  open: PropTypes.bool,
  inFiles: PropTypes.array,
};

const mapStateToProps = state => ({
  open: state.ui.modal === MODALS.UPLOAD,
});

const mapDispatchToProps = {
  defineGeometry,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(UploadDialog));
